import React from "react";
import { ArrowRight, Package, Check } from "lucide-react";

// Each theme defines the key surface + accent colors used across the site.
const THEMES = [
  {
    id: "teal",
    name: "Fresh Teal (Current)",
    heroBg: "#edfbf8",
    statBg: "#dbedf4",
    accent: "#2fb89c",
    accentText: "#ffffff",
    ink: "#1b2545",
    body: "#64748b",
    note: "Clean, trustworthy, money-fresh. Keeps large areas light for easy reading. Best all-round for a B2B audience.",
  },
  {
    id: "navy",
    name: "Bold Navy",
    heroBg: "#141d33",
    statBg: "#1b2545",
    accent: "#37c9aa",
    accentText: "#08131f",
    ink: "#ffffff",
    body: "#a9b6cc",
    note: "Premium, authoritative, high-contrast. Great for a serious, established feel \u2014 but heavier on the eyes for long pages.",
    dark: true,
  },
  {
    id: "gold",
    name: "Gold & Navy",
    heroBg: "#fffaf0",
    statBg: "#fdf1d6",
    accent: "#f5b301",
    accentText: "#1a2233",
    ink: "#16233f",
    body: "#5b6472",
    note: "Energetic \u2018deals & savings\u2019 vibe. Gold grabs attention on CTAs. Pair sparingly \u2014 too much yellow can feel loud.",
  },
  {
    id: "forest",
    name: "Deep Forest Green",
    heroBg: "#f5f6ef",
    statBg: "#e6ede2",
    accent: "#17714f",
    accentText: "#ffffff",
    ink: "#12291f",
    body: "#5c6b5f",
    note: "Wealth, stability, established. Warmer and more classic than teal. Excellent for trust; slightly less \u2018tech-fresh\u2019.",
  },
];

function Mockup({ t }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
      {/* Label bar */}
      <div className="flex items-center justify-between bg-white px-5 py-3">
        <h3 className="text-base font-bold text-navy-brand" style={{ fontFamily: "Sora" }}>{t.name}</h3>
        <div className="flex gap-1.5">
          <span className="h-5 w-5 rounded-full border border-slate-200" style={{ background: t.accent }} />
          <span className="h-5 w-5 rounded-full border border-slate-200" style={{ background: t.ink }} />
          <span className="h-5 w-5 rounded-full border border-slate-200" style={{ background: t.heroBg }} />
        </div>
      </div>

      {/* Hero band */}
      <div className="px-6 py-8" style={{ background: t.heroBg }}>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
          style={{ background: t.dark ? "rgba(255,255,255,0.08)" : "#ffffff", color: t.accent }}
        >
          <Package size={12} /> Inventory Liquidation
        </span>
        <h4 className="mt-3 text-2xl font-extrabold leading-tight" style={{ fontFamily: "Sora", color: t.ink }}>
          Turn <span style={{ color: t.accent }}>Overstock</span> Into Cash
        </h4>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: t.body }}>
          We buy surplus, closeouts and excess inventory outright — fast, discreet and hassle-free.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-semibold"
            style={{ background: t.accent, color: t.accentText }}
          >
            Request a Quote <ArrowRight size={14} />
          </span>
          <span
            className="rounded-full border px-5 py-2.5 text-[13px] font-semibold"
            style={{ borderColor: t.dark ? "rgba(255,255,255,0.25)" : t.ink, color: t.ink }}
          >
            Our Services
          </span>
        </div>
      </div>

      {/* Stat band */}
      <div className="grid grid-cols-3 gap-2 px-6 py-5" style={{ background: t.statBg }}>
        {[["28", "Years"], ["34+", "Countries"], ["1000s", "Pallets/yr"]].map(([v, l]) => (
          <div key={l} className="text-center">
            <p className="text-xl font-extrabold" style={{ fontFamily: "Sora", color: t.ink }}>{v}</p>
            <p className="text-[11px]" style={{ color: t.body }}>{l}</p>
          </div>
        ))}
      </div>

      {/* Card */}
      <div className="bg-white px-6 py-6">
        <div className="rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div
            className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl"
            style={{ background: t.accent, color: t.accentText }}
          >
            <Check size={18} />
          </div>
          <p className="text-sm font-bold" style={{ color: t.ink }}>Immediate Payment</p>
          <p className="mt-1 text-[12px]" style={{ color: t.body }}>Paid in full by wire on receipt of goods.</p>
        </div>
        <p className="mt-4 text-[12px] leading-relaxed" style={{ color: t.body }}>
          <span className="font-semibold" style={{ color: t.ink }}>Why it works: </span>{t.note}
        </p>
      </div>
    </div>
  );
}

export default function ThemePreview() {
  return (
    <main className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="text-center">
        <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">Color Concepts</span>
        <h1 className="mt-3 text-4xl font-extrabold text-navy-brand">Pick a Color Direction</h1>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] text-slate-500">
          The same page rendered in four palettes so you can compare side by side. Each mini mockup shows
          the hero, buttons, stat band and a card exactly as they'd appear site-wide.
        </p>
      </div>

      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {THEMES.map((t) => (
          <Mockup key={t.id} t={t} />
        ))}
      </div>

      <div className="mt-12 rounded-2xl bg-teal-brand/10 p-6 text-center">
        <p className="text-sm font-medium text-navy-brand">
          Tell me which one you like — e.g. <strong>&ldquo;Bold Navy&rdquo;</strong>, <strong>&ldquo;Gold &amp; Navy&rdquo;</strong> or <strong>&ldquo;Deep Forest Green&rdquo;</strong> —
          and I&rsquo;ll roll it out across the whole site. I can also fine-tune any shade.
        </p>
      </div>
    </main>
  );
}
